import {
  HANDOFF_EVENT_KEY,
  HANDOFF_IMITATING_KEY,
  HANDOFF_TAB_KEY,
  HANDOFF_TOKEN_KEY,
  clearHandoff,
  isHandoffTab,
  tabScopedStorage,
} from './preauth';

export { HANDOFF_EVENT_KEY };

export const IMPERSONATION_HEADER = 'X-Impersonate-Sub';
export const IMPERSONATION_STORAGE_KEY = 'bamboo.impersonation';
export const SESSION_SUBJECT_KEY = 'bamboo.session.subject';
export const IMPERSONATION_ADMIN_PATH = '/admin/impersonation';

export type ImpersonationTarget = {
  sub: string;
  email: string;
  districtName: string | null;
};

type Navigate = (path: string) => void;

const defaultNavigate: Navigate = (path) => {
  window.location.assign(path);
};

export function getImpersonation(): ImpersonationTarget | null {
  try {
    const raw = tabScopedStorage().getItem(IMPERSONATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ImpersonationTarget> | null;
    if (!parsed || typeof parsed.sub !== 'string' || !parsed.sub || typeof parsed.email !== 'string') {
      return null;
    }
    return { sub: parsed.sub, email: parsed.email, districtName: parsed.districtName ?? null };
  } catch {
    return null;
  }
}

const DISTRICT_CACHE_PREFIXES = ['bamboo.', 'pe-generation:'];
const SESSION_STATE_KEYS = new Set([IMPERSONATION_STORAGE_KEY, SESSION_SUBJECT_KEY]);
const RETAINED_KEYS = new Set([...SESSION_STATE_KEYS, HANDOFF_EVENT_KEY, HANDOFF_TOKEN_KEY, HANDOFF_IMITATING_KEY, HANDOFF_TAB_KEY]);

function clearDistrictCaches(storage: Storage): void {
  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (key && !RETAINED_KEYS.has(key) && DISTRICT_CACHE_PREFIXES.some((p) => key.startsWith(p))) {
      storage.removeItem(key);
    }
  }
}

export function clearTabDistrictCaches(): void {
  try {
    clearDistrictCaches(window.sessionStorage);
  } catch (error) {
    console.warn('impersonation: could not clear sessionStorage', error);
  }
}

// A handoff tab's district settings live in its own sessionStorage, so its
// transitions must not sweep the shared settings an Amplify tab relies on.
function clearOwnDistrictCaches(): void {
  if (isHandoffTab()) clearTabDistrictCaches();
  else clearDistrictScopedCaches();
}

export function clearDistrictScopedCaches(): void {
  for (const name of ['localStorage', 'sessionStorage'] as const) {
    try {
      clearDistrictCaches(window[name]);
    } catch (error) {
      console.warn(`impersonation: could not clear ${name}`, error);
    }
  }
}

type Cleanup = () => void;

const cleanups = new Set<Cleanup>();
let purgeOnUnload = false;

export function registerImpersonationCleanup(cleanup: Cleanup): () => void {
  cleanups.add(cleanup);
  return () => {
    cleanups.delete(cleanup);
  };
}

function runImpersonationCleanups(): void {
  for (const cleanup of cleanups) {
    try {
      cleanup();
    } catch (error) {
      console.warn('impersonation: cleanup failed', error);
    }
  }
}

export function startImpersonation(target: ImpersonationTarget, navigate: Navigate = defaultNavigate): boolean {
  runImpersonationCleanups();
  clearOwnDistrictCaches();
  purgeOnUnload = true;
  try {
    tabScopedStorage().setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(target));
  } catch {
    return false;
  }
  navigate('/');
  return true;
}

export function clearImpersonation(): void {
  try {
    tabScopedStorage().removeItem(IMPERSONATION_STORAGE_KEY);
  } catch (error) {
    console.warn('impersonation: could not clear session storage', error);
  }
}

export function abandonImpersonation(): void {
  runImpersonationCleanups();
  clearOwnDistrictCaches();
  clearImpersonation();
  purgeOnUnload = true;
}

export function stopImpersonation(navigate: Navigate = defaultNavigate): void {
  abandonImpersonation();
  navigate(IMPERSONATION_ADMIN_PATH);
}

export function handlePageRestore(event: { persisted: boolean }, reload: () => void): void {
  if (event.persisted) reload();
}

export function handlePageHide(): void {
  if (!purgeOnUnload) return;
  clearOwnDistrictCaches();
}

export function installBackForwardGuard(reload: () => void = () => window.location.reload()): () => void {
  const onShow = (event: Event) => handlePageRestore(event as PageTransitionEvent, reload);
  const onHide = () => handlePageHide();
  window.addEventListener('pageshow', onShow);
  window.addEventListener('pagehide', onHide);
  return () => {
    window.removeEventListener('pageshow', onShow);
    window.removeEventListener('pagehide', onHide);
  };
}

export function handleStorageChange(
  event: { key: string | null; newValue: string | null; oldValue: string | null },
  reload: () => void,
): void {
  if (event.key !== null) {
    if (!SESSION_STATE_KEYS.has(event.key) || event.newValue === event.oldValue) return;
    if (event.key === IMPERSONATION_STORAGE_KEY && isHandoffTab()) return;
  }
  runImpersonationCleanups();
  clearDistrictScopedCaches();
  if (endsHandoff(event.key)) clearHandoff();
  else if (event.key !== IMPERSONATION_STORAGE_KEY) clearHandoffMarker();
  reload();
}

// Another tab signing out, claiming a different subject, or changing the
// impersonation an imitated handoff depends on all end this tab's handoff;
// otherwise two tabs with different subjects reload each other forever.
function endsHandoff(key: string | null): boolean {
  if (key === null || key === SESSION_SUBJECT_KEY) return true;
  return key === IMPERSONATION_STORAGE_KEY && handoffCarriesImitation();
}

function handoffCarriesImitation(): boolean {
  try {
    return sessionStorage.getItem(HANDOFF_IMITATING_KEY) !== null;
  } catch {
    return false;
  }
}

export function clearHandoffMarker(): void {
  try {
    sessionStorage.removeItem(HANDOFF_EVENT_KEY);
  } catch {
    return;
  }
}

export function installCrossTabSync(reload: () => void = () => window.location.reload()): () => void {
  const listener = (event: Event) => handleStorageChange(event as StorageEvent, reload);
  window.addEventListener('storage', listener);
  return () => window.removeEventListener('storage', listener);
}

export function authHeadersFor(token: string, target: ImpersonationTarget | null): Record<string, string> {
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (target) headers[IMPERSONATION_HEADER] = target.sub;
  return headers;
}

export function authHeaders(token: string): Record<string, string> {
  return authHeadersFor(token, getImpersonation());
}
